import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { Label } from '@repo/ui/src/components/ui/label';
import { Slider } from '@repo/ui/src/components/ui/slider';
import { cropToPngBlob, type CropPixels } from '@repo/ui/src/lib/cropImage';

interface MerchantLogoCropDialogProps {
  open: boolean;
  /** Object URL (or data URL) of the file the operator just picked. Owned by
   *  the caller — this dialog only reads it, never revokes it. */
  imageSrc: string;
  onOpenChange: (open: boolean) => void;
  /** Cropped 1:1 PNG, ready for the caller to POST as multipart. */
  onCropped: (blob: Blob) => void;
}

/**
 * Admin-only logo cropper. Unlike the dashboard's `LogoCropDialog`, this does
 * NOT pick a file (the caller already has `imageSrc`) and does NOT upload
 * anything itself — it just crops and hands back a `Blob`. apps/admin has no
 * i18n, so all copy here is hardcoded Vietnamese.
 */
export function MerchantLogoCropDialog({ open, imageSrc, onOpenChange, onCropped }: MerchantLogoCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState<CropPixels | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cropping, setCropping] = useState(false);

  const onCropComplete = useCallback((_area: unknown, areaPixels: CropPixels) => {
    setCropPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!cropPixels) return;
    setCropping(true);
    setError(null);
    try {
      const blob = await cropToPngBlob(imageSrc, cropPixels);
      onCropped(blob);
    } catch {
      setError('Không cắt được ảnh. Vui lòng thử lại.');
      setCropping(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Ignore close requests (X, Escape, overlay click) mid-crop — the
        // caller's onCropped callback still needs to fire once cropToPngBlob
        // resolves, and yanking the dialog away would strand that state.
        if (!next && cropping) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cắt logo merchant</DialogTitle>
          <DialogDescription>Chọn vùng ảnh vuông 1:1 sẽ hiển thị trên trang SePay.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative h-64 w-full bg-surface-100">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="space-y-1.5">
            <Label id="merchant-logo-zoom-label">Phóng to</Label>
            <Slider
              aria-labelledby="merchant-logo-zoom-label"
              min={1}
              max={3}
              step={0.01}
              value={[zoom]}
              onValueChange={([next]) => setZoom(next ?? 1)}
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={cropping}>
            Huỷ
          </Button>
          <Button onClick={handleConfirm} disabled={!cropPixels || cropping}>
            {cropping ? 'Đang cắt…' : 'Dùng ảnh này'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
